import EditTradeContext from "@shared/features/trades/EditTrade/EditTrade.context";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  AddAmountModalProps,
  EditOrderData,
} from "@shared/features/trades/EditTrade/types";
import { LanguageNameSpaces, OrderStatus } from "@shared/types";
import { ErrorTypes, OrderTypes } from "@shared/features/trades/common/types";
import { getNumOrdersValidationMessage } from "@shared/features/tradingTerminal/common/utils";
import { getAmountValidationMessage } from "@shared/features/trades/common/utils";
import { addFloats, multiplyFloats, toFixed } from "@shared/utils/numbers";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export const useOrdersSection = () => {
  const {
    addOrder,
    takeProfitOrders,
    ordersData: responseData,
    ordersErrors,
  } = useContext(EditTradeContext) || {};
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [addOrderModalOpen, setAddOrderModalOpen] = useState(false);

  const getAddOrderDisabledMessage = (
    orderType: OrderTypes,
    orders?: EditOrderData[]
  ) => {
    const isEntry = orderType === OrderTypes.ENTRY;
    const isTakeProfitFilled = takeProfitOrders?.some(
      (order) => order.filled_amount > 0
    );
    if (isEntry && isTakeProfitFilled) return t("warnings.take_profit_reached");
    return getNumOrdersValidationMessage(
      orders?.filter((order) => order.status !== OrderStatus.CANCELLED)
        .length || 0,
      orderType,
      responseData?.data?.breakout,
      responseData?.limits
    );
  };

  const onAddOrderClick = (orderType: OrderTypes, order: EditOrderData) => {
    if (orderType) {
      addOrder?.(orderType, order);
    }
  };

  const takeProfitsSum = useMemo(() => {
    const ratioSum =
      takeProfitOrders?.reduce(
        (sum, order) => addFloats(sum, order.edit_ratio),
        0
      ) || 0;

    return toFixed(multiplyFloats(ratioSum, 100), 2);
  }, [takeProfitOrders]);

  const errorsExtraData = {
    [ErrorTypes.EDIT_TRADE_SUM_OF_RATIOS]: `(currently ${takeProfitsSum}%)`,
  };

  return {
    addOrderModalOpen,
    setAddOrderModalOpen,
    getAddOrderDisabledMessage,
    onAddOrderClick,
    ordersErrors,
    errorsExtraData,
  };
};

export const useAddAmountModal = ({
  closeModal,
  amountData,
  onSave,
  validateMinAmount,
}: AddAmountModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { ordersData } = useContext(EditTradeContext) || {};
  const [amountInputValue, setAmountInputValue] = useState<string | undefined>(
    "0"
  );
  const numberValue = parseFloat(amountInputValue || "0");

  const onOkClick = useCallback(() => {
    onSave?.(numberValue);
    setAmountInputValue(amountInputValue);
    closeModal();
  }, [amountInputValue]);

  let showSlider = true;
  const availableAmount = amountData.getAvailable();
  if (ordersData?.data.number_of_active_entries) {
    showSlider = false;
  }

  const isError =
    (validateMinAmount && numberValue < (amountData.min || 0)) ||
    numberValue > availableAmount;

  const modalData = {
    title: t("labels.add_amount"),
    disabled: !amountInputValue || amountInputValue === "0" || isError,
    text: t("modals.choose_coin_amount_to_add", { coin: amountData.coin }),
    validationMessage: getAmountValidationMessage(
      validateMinAmount ? amountData.min : 0,
      availableAmount
    ),
  };

  return {
    onOkClick,
    isError,
    showSlider,
    availableAmount,
    amountInputValue,
    setAmountInputValue,
    modalData,
  };
};
